import React from 'react';
import { Mail } from 'lucide-react';
import { useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Seo } from '../../components/seo/Seo';
import { PublicLayout } from '../../components/public/PublicLayout';
import { Container, FaqAccordion, CtaBand } from '../../components/public/PublicSections';
import { SectionHeading } from '../../components/public/SectionHeading';
import { FAQS, FAQ_PAGE, HOME, LAYOUT_COPY } from '../../seo/publicContent';
import { pageMeta, pagePath, alternatesFor, SUPPORT_EMAIL } from '../../seo/seoConfig.mjs';
import { breadcrumbSchema, faqSchema } from '../../seo/structuredData';

export default function PublicFaqPage({ lang }) {
  const { user } = useAuth();
  const copy = FAQ_PAGE[lang];
  const meta = pageMeta('faq', lang);
  const faqs = FAQS[lang];
  const appHref = user ? (user.onboarding_complete ? '/dashboard' : '/onboarding') : '/auth';

  return (
    <PublicLayout lang={lang} pageKey="faq">
      <Seo
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        path={pagePath('faq', lang)}
        lang={lang}
        alternates={alternatesFor('faq')}
        jsonLd={[
          breadcrumbSchema([
            { name: LAYOUT_COPY[lang].nav.home, path: pagePath('home', lang) },
            { name: LAYOUT_COPY[lang].nav.faq, path: pagePath('faq', lang) },
          ]),
          faqSchema(faqs),
        ]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(600px circle at 15% 0%, rgba(243,179,61,0.12), transparent 55%)' }} />
        <Container className="relative pt-14 pb-12 sm:pt-20 sm:pb-16">
          <SectionHeading as="h1" eyebrow={copy.eyebrow} title={copy.h1} sub={copy.sub} testId="faq-page-heading" />
        </Container>
      </section>

      <section className="pb-16 sm:pb-24" aria-label={copy.eyebrow}>
        <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <FaqAccordion faqs={faqs} testId="faq-page-accordion" />
          </div>
          <aside className="lg:col-span-4">
            <div className="rounded-2xl border border-lux-gold/20 bg-lux-gold/5 p-6 lg:sticky lg:top-24" data-testid="faq-contact-card">
              <Mail className="w-6 h-6 text-[var(--lux-gold)]" />
              <h2 className="mt-4 text-lg font-semibold text-white">{copy.moreTitle}</h2>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{copy.moreSub}</p>
              <Button asChild variant="outline" className="mt-5 h-10 bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)] hover:border-lux-gold/40 transition-[background-color,border-color] duration-200" data-testid="faq-contact-button">
                <a href={`mailto:${SUPPORT_EMAIL}`}>{copy.moreCta}</a>
              </Button>
            </div>
          </aside>
        </Container>
      </section>

      <CtaBand title={HOME[lang].finalTitle} sub={HOME[lang].finalSub} ctaLabel={HOME[lang].ctaPrimary} to={appHref} testId="faq-final-cta" />
    </PublicLayout>
  );
}
